import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { toast } from "Components/DesignSystem/Toast/useToast";
// release-sept-29 merge (2026-09-15): react-toastify is no longer a dependency — the app has one
// toast system (design-system § 116). Same calls, same messages; they land in the DS ToastRegion.
import { AppContext } from "Context/AppContext";
import NoDataComponent from "Components/Common/NoDataComponent";

import Commend3Header from "./Components/Commend3Header";
import Commend3Tabs from "./Components/Commend3Tabs";
import ModelSearch from "./Components/ModelSearch";
import FilterButton from "./Components/FilterButton";
import ModelFilterPanel from "./Components/ModelFilterPanel";
import ModelsTable from "./Components/ModelsTable";
import ModelsPagination from "./Components/ModelsPagination";
import StrategiesTab from "./Components/StrategiesTab";
import CreateModelDialog from "./Components/CreateModelDialog";
import DeleteModelDialog from "./Components/DeleteModelDialog";
import { Commend3RowAction, Commend3RowActionItem } from "./Components/RowActionsMenu";

import {
  cloneCommendModel,
  createCommendModel,
  deleteCommendModel,
  getCommendModels,
} from "./Commend3Services/Commend3Services";
import { Commend3Model, Commend3ModelType, Commend3SortOrder } from "./Types";
import {
  applyFacetPicks,
  Commend3ClientSortField,
  Commend3FacetPicks,
  countPicks,
  matchesSearch,
  sortModels,
} from "./Commend3Facets";
import {
  COMMEND3_PATH,
  ALL_MODELS_LIMIT,
  DEFAULT_PAGE_SIZE,
  MODEL_SEARCH_DEBOUNCE_MS,
  MODELS_TAB,
  NOT_YET_AVAILABLE,
  RECOMMENDER_CREATE,
  RECOMMENDER_DELETE,
  RECOMMENDER_READ,
  ROW_ACTION_BUSY,
  ROW_ACTION_NO_CREATE_PERMISSION,
  ROW_ACTION_NO_DELETE_PERMISSION,
  STRATEGIES_TAB,
} from "./Commend3Constants";

const Commend3Component = () => {
  const appData = useContext(AppContext);
  const navigate = useNavigate();

  const [allModels, setAllModels] = useState<Commend3Model[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  const [activeTab, setActiveTab] = useState<string>(MODELS_TAB);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sortField, setSortField] = useState<Commend3ClientSortField>("updated");
  const [sortOrder, setSortOrder] = useState<Commend3SortOrder>("desc");

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [facetPicks, setFacetPicks] = useState<Commend3FacetPicks>({});
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [openFacets, setOpenFacets] = useState<Record<string, boolean>>({ promotion: true });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [deleteTarget, setDeleteTarget] = useState<Commend3Model | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const initialLoadRef = useRef<boolean>(true);

  const channelKey = appData?.channelKey;
  const readPermission = appData.pm.checkPermission(RECOMMENDER_READ);
  const createPermission = appData.pm.checkPermission(RECOMMENDER_CREATE);
  const deletePermission = appData.pm.checkPermission(RECOMMENDER_DELETE);
  const industryId = appData?.get_selected_industry()?.id;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, MODEL_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchModels = useCallback(async () => {
    if (!channelKey) return;
    setLoading(true);
    try {
      const res = await getCommendModels(channelKey, {
        page: 1,
        limit: ALL_MODELS_LIMIT,
        sort_by: "updated_at",
        sort_order: "desc",
      });
      if (res?.done) {
        setAllModels(res?.data?.data ?? []);
      } else {
        toast.error(res?.message ?? "Failed to fetch models");
      }
    } catch (error) {
      console.error("Error fetching Commend 3.0 models", error);
      toast.error("Failed to fetch models. Please try again.");
    } finally {
      setLoading(false);
      if (initialLoadRef.current) {
        initialLoadRef.current = false;
        setInitialLoad(false);
      }
    }
  }, [channelKey]);

  useEffect(() => {
    if (!readPermission) {
      navigate("/workspace/module/permission/denied");
      return;
    }
    fetchModels();
  }, [readPermission, fetchModels, navigate]);

  useEffect(() => {
    setSelectedIds([]);
  }, [page, limit, facetPicks, searchTerm, sortField, sortOrder, channelKey]);

  const totalPicks = useMemo(() => countPicks(facetPicks), [facetPicks]);

  const filteredModels = useMemo(() => {
    const faceted = applyFacetPicks(allModels, facetPicks);
    const searched = faceted.filter((model) => matchesSearch(model, searchTerm));
    return sortModels(searched, sortField, sortOrder);
  }, [allModels, facetPicks, searchTerm, sortField, sortOrder]);

  const totalCount = filteredModels.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / limit));
  const safePage = Math.min(page, pageCount);
  const models = useMemo(
    () => filteredModels.slice((safePage - 1) * limit, safePage * limit),
    [filteredModels, safePage, limit]
  );

  const handleSortChange = (field: Commend3ClientSortField, order: Commend3SortOrder) => {
    setSortField(field);
    setSortOrder(order);
    setPage(1);
  };

  const handlePickFilter = (facetKey: string, value: string) => {
    setFacetPicks((current) => {
      const selected = current[facetKey] ?? [];
      const next = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : selected.concat([value]);
      const merged = { ...current, [facetKey]: next };
      if (!next.length) delete merged[facetKey];
      return merged;
    });
    setPage(1);
  };

  const handleToggleRow = (model: Commend3Model) => {
    setSelectedIds((current) =>
      current.includes(model.uid)
        ? current.filter((id) => id !== model.uid)
        : current.concat([model.uid])
    );
  };

  const handleOpenModel = (model: Commend3Model) => {
    navigate(`${COMMEND3_PATH}/model/${model.uid}`);
  };

  const buildRowActions = (model: Commend3Model): Commend3RowActionItem[] => {
    const busy = busyId === model.uid;

    const actions: Commend3RowActionItem[] = [
      {
        type: "copy",
        label: "Copy",
        disabled: !createPermission || busy,
        hint: busy ? ROW_ACTION_BUSY : ROW_ACTION_NO_CREATE_PERMISSION,
      },
    ];

    actions.push({
      type: "unpromote",
      label: "Unpromote",
      disabled: true,
      hint: NOT_YET_AVAILABLE,
    });

    actions.push({
      type: "delete",
      label: "Delete",
      disabled: !deletePermission || busy,
      danger: true,
      hint: busy ? ROW_ACTION_BUSY : ROW_ACTION_NO_DELETE_PERMISSION,
    });

    return actions;
  };

  const handleCopy = async (model: Commend3Model) => {
    if (!channelKey) return;
    try {
      setBusyId(model.uid);
      const res = await cloneCommendModel(channelKey, model.uid);
      if (res?.done) {
        toast.success("Model copied successfully");
        fetchModels();
      } else {
        toast.error(res?.message ?? "Failed to copy model");
      }
    } catch (error) {
      console.error("Error copying Commend 3.0 model", error);
      toast.error("Failed to copy model. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRowAction = (model: Commend3Model, action: Commend3RowAction) => {
    if (action === "copy") {
      handleCopy(model);
      return;
    }
    if (action === "delete") {
      setDeleteTarget(model);
    }
  };

  const handleCreateConfirm = async (name: string, type: string) => {
    if (!channelKey || !industryId) {
      toast.error("Environment or industry is not selected.");
      return;
    }
    try {
      setCreating(true);
      const res = await createCommendModel({
        name,
        type: type as Commend3ModelType,
        channel_id: channelKey,
        industry_id: industryId,
      });
      if (res?.done) {
        toast.success("Model created successfully");
        setShowCreate(false);
        const createdId = res?.data?.uid;
        if (createdId) {
          navigate(`${COMMEND3_PATH}/model/${createdId}`);
          return;
        }
        fetchModels();
      } else {
        toast.error(res?.message ?? "Failed to create model");
      }
    } catch (error) {
      console.error("Error creating Commend 3.0 model", error);
      toast.error("Failed to create model. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !channelKey) return;
    try {
      setDeleting(true);
      setBusyId(deleteTarget.uid);
      const res = await deleteCommendModel(channelKey, deleteTarget.uid);
      if (res?.done) {
        toast.success("Model deleted successfully");
        setDeleteTarget(null);
        fetchModels();
      } else {
        toast.error(res?.message ?? "Failed to delete model");
      }
    } catch (error) {
      console.error("Error deleting Commend 3.0 model", error);
      toast.error("Failed to delete model. Please try again.");
    } finally {
      setDeleting(false);
      setBusyId(null);
    }
  };

  return (
    <div
      id="commend3-page"
      className="tw-flex tw-h-full tw-flex-col"
      style={{ background: "#FAFBFD" }}
    >
      <Commend3Header
        createEnabled={createPermission}
        onCreate={() => setShowCreate(true)}
        createUnavailableReason="You do not have permission to create a model"
      />

      <Commend3Tabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === STRATEGIES_TAB ? (
        <StrategiesTab
          channelKey={channelKey ?? ""}
          generateEnabled={false}
          generateUnavailableReason={NOT_YET_AVAILABLE}
        />
      ) : (
        <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3.5 tw-overflow-y-auto tw-px-[22px] tw-py-5">
          <div
            className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-4 tw-pb-3.5"
            style={{ borderBottom: "1px solid #E3E5EC" }}
          >
            <ModelSearch value={searchInput} onChange={setSearchInput} />
            <FilterButton
              open={filterOpen}
              activeCount={totalPicks}
              onClick={() => setFilterOpen((current) => !current)}
            />
          </div>

          <ModelsTable
            models={models}
            loading={loading}
            sortField={sortField}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            selectedIds={selectedIds}
            onToggleRow={handleToggleRow}
            onOpenModel={handleOpenModel}
            buildRowActions={buildRowActions}
            onRowAction={handleRowAction}
            emptyContent={
              initialLoad ? null : <NoDataComponent title="No models created" />
            }
          />

          <ModelsPagination
            page={safePage}
            pageSize={limit}
            total={totalCount}
            onPage={setPage}
            onPageSize={(next) => {
              setLimit(next);
              setPage(1);
            }}
          />
        </div>
      )}

      <ModelFilterPanel
        isOpen={filterOpen}
        picks={facetPicks}
        openFacets={openFacets}
        totalPicks={totalPicks}
        resultCount={totalCount}
        onToggleFacet={(key) =>
          setOpenFacets((current) => ({ ...current, [key]: !current[key] }))
        }
        onPick={handlePickFilter}
        onClearAll={() => {
          setFacetPicks({});
          setPage(1);
        }}
        onClose={() => setFilterOpen(false)}
      />

      <CreateModelDialog
        isOpen={showCreate}
        creating={creating}
        onClose={() => setShowCreate(false)}
        onConfirm={handleCreateConfirm}
      />

      <DeleteModelDialog
        model={deleteTarget}
        deleting={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default Commend3Component;
